<template>
  <div>
    <!-- Mockup bar: role switcher + back -->
    <div class="mockup-bar">
      <span class="mockup-badge">MOCKUP</span>
      <span class="mockup-bar-sep">·</span>
      <span class="mockup-bar-label">Viewing as:</span>
      <button
        v-for="r in roles"
        :key="r.id"
        class="role-btn"
        :class="{ active: currentRole.id === r.id }"
        @click="currentRole = r"
      >{{ r.label }}</button>
      <router-link to="/wireframes/dashboard/assignments-mockup" class="mockup-full">Assignments editor (mock) -&gt;</router-link>
      <router-link to="/" class="mockup-back">← Back to app</router-link>
    </div>

    <div class="dashboard-wrap">
      <div class="dashboard-header">
        <div class="heading">Visit dashboard</div>
      </div>

      <!-- Filters: visit type + status -->
      <div class="filter-row">
        <div class="filter-group">
          <span class="filter-label">Visit type</span>
          <div class="filter-chips">
            <button
              v-for="vt in visitTypeOptions"
              :key="vt.value"
              class="filter-chip"
              :class="{ active: visitTypeFilter === vt.value }"
              @click="visitTypeFilter = vt.value"
            >{{ vt.label }}</button>
          </div>
        </div>
        <div class="filter-group">
          <span class="filter-label">Status</span>
          <div class="filter-chips">
            <button
              v-for="st in statusFilterOptions"
              :key="st.value"
              class="filter-chip"
              :class="{ active: statusFilter === st.value }"
              @click="statusFilter = st.value"
            >{{ st.label }}</button>
          </div>
        </div>
      </div>

      <!-- List + detail pane -->
      <div class="browse-layout">
        <div class="visit-list-col">
          <div class="search-row">
            <input
              v-model="subjectIdSearch"
              type="text"
              class="subject-search"
              placeholder="Search subject ID..."
            />
          </div>
          <div class="visit-list">
          <div
            v-for="visit in filteredVisits"
            :key="visit.id"
            class="visit-row"
            :class="{ active: selectedVisit && selectedVisit.id === visit.id }"
            @click="selectVisit(visit)"
          >
            <div class="visit-row-top">
              <span class="visit-pid">{{ visit.participantId }}</span>
              <span class="visit-type">{{ visit.type }}</span>
            </div>
            <div class="visit-row-bottom">
              <span class="visit-date">{{ visit.date }}</span>
              <span class="visit-status" :class="visit.status">{{ visit.statusLabel }}</span>
            </div>
          </div>
          <div v-if="filteredVisits.length === 0" class="visit-list-empty">No visits match the current filters.</div>
          </div>
        </div>

        <div class="visit-detail" v-if="selectedVisit">
          <div class="detail-header">
            <div>
              <div class="detail-title">{{ selectedVisit.participantId }} — {{ selectedVisit.type }}</div>
              <div class="detail-sub">{{ selectedVisit.date }}</div>
            </div>
            <span class="visit-status" :class="selectedVisit.status">{{ selectedVisit.statusLabel }}</span>
          </div>

          <div class="tabs">
            <button
              v-for="tab in tabs"
              :key="tab.id"
              class="tab-btn"
              :class="{ active: activeTab === tab.id }"
              @click="activeTab = tab.id"
            >{{ tab.label }}</button>
          </div>

          <div v-if="activeTab === 'tasks'" class="tab-panel">
            <table class="task-table">
              <thead>
                <tr>
                  <th>Task</th>
                  <th>Status</th>
                  <th>Open requests</th>
                  <th>Go to task</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="task in selectedVisit.tasks" :key="task.name">
                  <td>{{ task.name }}</td>
                  <td><span class="task-status" :class="task.status">{{ task.statusLabel }}</span></td>
                  <td>
                    <span v-if="task.openRequests > 0" class="open-req-chip">⚑ {{ task.openRequests }}</span>
                    <span v-else class="none-chip">—</span>
                  </td>
                  <td>
                    <button type="button" class="go-to-task-link" title="Mock: would open this task">Go to task</button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-if="activeTab === 'history'" class="tab-panel">
            <div v-if="selectedVisit.history.length === 0" class="empty-state">No changes recorded yet.</div>
            <div
              v-for="event in selectedVisit.history"
              :key="event.id"
              class="history-event"
            >
              <div class="he-left">
                <div class="he-avatar" :style="{ background: event.actorColor }">{{ event.initials }}</div>
                <div class="he-spine"></div>
              </div>
              <div class="he-body">
                <div class="he-meta">
                  <span class="he-actor">{{ event.actor }}</span>
                  <span class="he-tag" :class="event.type">{{ eventLabel(event.type) }}</span>
                  <span class="he-time">{{ event.time }}</span>
                </div>
                <div class="he-text">{{ event.text }}</div>
                <div v-if="event.from || event.to" class="he-change">
                  <span class="he-mark" :class="event.from">{{ event.from || '—' }}</span>
                  <span class="he-arrow">→</span>
                  <span class="he-mark" :class="event.to">{{ event.to || '—' }}</span>
                  <span class="he-task-ref">{{ event.task }}</span>
                </div>
              </div>
            </div>
          </div>

          <div v-if="activeTab === 'comments'" class="tab-panel">
            <div v-if="selectedVisit.comments.length === 0" class="empty-state">No comments yet.</div>
            <div
              v-for="c in selectedVisit.comments"
              :key="c.id"
              class="comment-item"
            >
              <div class="comment-avatar" :style="{ backgroundColor: c.avatarColor }">{{ c.initials }}</div>
              <div class="comment-body">
                <div class="comment-meta">
                  <span class="comment-author">{{ c.author }}</span>
                  <span class="comment-task">{{ c.task }}</span>
                  <span class="comment-time">{{ c.time }}</span>
                </div>
                <div class="comment-text">{{ c.text }}</div>
              </div>
            </div>
          </div>
        </div>

        <div class="visit-detail empty-detail" v-else>
          <p>Select a visit to see details.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DashboardWireframe',
  data() {
    return {
      currentRole: { id: 'interviewer', label: 'Interviewer' },
      roles: [
        { id: 'interviewer', label: 'Interviewer' },
        { id: 'qa', label: 'QA' }
      ],
      visitTypeFilter: 'all',
      visitTypeOptions: [
        { value: 'all', label: 'All' },
        { value: 'Biennial', label: 'Biennial' },
        { value: 'Baseline', label: 'Baseline' }
      ],
      statusFilter: 'all',
      subjectIdSearch: '',
      selectedVisit: null,
      activeTab: 'tasks',
      tabs: [
        { id: 'tasks', label: 'Tasks' },
        { id: 'history', label: 'History / Changes' },
        { id: 'comments', label: 'Comments' }
      ],
      allTaskNames: [
        'Clock Drawing',
        'CASI',
        'Verbal Fluency - Supermarket',
        'Verbal Fluency - Letter F',
        'Trail Making A',
        'Trail Making B'
      ],
      visits: [
        {
          id: 1, participantId: 'ACT001', type: 'Biennial',
          date: 'Feb 10, 2025', status: 'complete', statusLabel: 'Complete',
          tasks: [
            { name: 'Clock Drawing', status: 'scored', statusLabel: 'Scored', openRequests: 0 },
            { name: 'CASI', status: 'scored', statusLabel: 'Scored', openRequests: 0 },
            { name: 'Verbal Fluency - Supermarket', status: 'scored', statusLabel: 'Scored', openRequests: 0 },
            { name: 'Verbal Fluency - Letter F', status: 'scored', statusLabel: 'Scored', openRequests: 0 },
            { name: 'Trail Making A', status: 'scored', statusLabel: 'Scored', openRequests: 0 },
            { name: 'Trail Making B', status: 'scored', statusLabel: 'Scored', openRequests: 0 },
          ],
          history: [
            { id: 'h1', actor: 'Interviewer', initials: 'IN', actorColor: '#317391', type: 'score-entry', time: 'Feb 10 · 9:22 AM', text: 'Scored VF — Supermarket.', from: null, to: null, task: '' },
            { id: 'h2', actor: 'Reviewer', initials: 'RV', actorColor: '#7c3aed', type: 'request', time: 'Feb 12 · 2:14 PM', text: 'Requested score change on VF — Supermarket.', from: 'C', to: 'I', task: 'VF — Supermarket' },
            { id: 'h3', actor: 'Interviewer', initials: 'IN', actorColor: '#317391', type: 'accepted', time: 'Feb 13 · 10:01 AM', text: 'Accepted change request for VF — Supermarket.', from: 'C', to: 'I', task: 'VF — Supermarket' },
          ],
          comments: [
            { id: 'c1', author: 'Interviewer', initials: 'IN', avatarColor: '#317391', task: 'VF — Supermarket', time: 'Feb 10 · 9:25 AM', text: 'Participant paused before apple juice — worth reviewing.' },
          ]
        },
        {
          id: 2, participantId: 'ACT002', type: 'Baseline',
          date: 'Feb 14, 2025', status: 'submitted', statusLabel: 'Ready for QA',
          tasks: [
            { name: 'Clock Drawing', status: 'scored', statusLabel: 'Scored', openRequests: 0 },
            { name: 'CASI', status: 'scored', statusLabel: 'Scored', openRequests: 0 },
            { name: 'Verbal Fluency - Supermarket', status: 'scored', statusLabel: 'Scored', openRequests: 0 },
            { name: 'Verbal Fluency - Letter F', status: 'scored', statusLabel: 'Scored', openRequests: 0 },
            { name: 'Trail Making A', status: 'scored', statusLabel: 'Scored', openRequests: 0 },
            { name: 'Trail Making B', status: 'scored', statusLabel: 'Scored', openRequests: 0 },
          ],
          history: [
            { id: 'h1', actor: 'Interviewer', initials: 'IN', actorColor: '#317391', type: 'score-entry', time: 'Feb 14 · 11:00 AM', text: 'Scored VF — Supermarket.', from: null, to: null, task: '' },
            { id: 'h2', actor: 'Interviewer', initials: 'IN', actorColor: '#317391', type: 'comment', time: 'Feb 14 · 11:05 AM', text: 'Submitted visit for QA review.', from: null, to: null, task: '' },
          ],
          comments: []
        },
        {
          id: 3, participantId: 'ACT003', type: 'Biennial',
          date: 'Feb 18, 2025', status: 'review-needed', statusLabel: 'Review needed',
          tasks: [
            { name: 'Clock Drawing', status: 'scored', statusLabel: 'Scored', openRequests: 0 },
            { name: 'CASI', status: 'scored', statusLabel: 'Scored', openRequests: 0 },
            { name: 'Verbal Fluency - Supermarket', status: 'scored', statusLabel: 'Scored', openRequests: 1 },
            { name: 'Verbal Fluency - Letter F', status: 'scored', statusLabel: 'Scored', openRequests: 0 },
            { name: 'Trail Making A', status: 'scored', statusLabel: 'Scored', openRequests: 0 },
            { name: 'Trail Making B', status: 'scored', statusLabel: 'Scored', openRequests: 0 },
          ],
          history: [
            { id: 'h1', actor: 'Interviewer', initials: 'IN', actorColor: '#317391', type: 'score-entry', time: 'Feb 18 · 9:00 AM', text: 'Scored VF — Supermarket.', from: null, to: null, task: '' },
            { id: 'h2', actor: 'Reviewer', initials: 'RV', actorColor: '#7c3aed', type: 'request', time: 'Feb 19 · 3:00 PM', text: 'Sent back: please fix apple juice mark.', from: 'C', to: 'I', task: 'VF — Supermarket' },
          ],
          comments: [
            { id: 'c1', author: 'Reviewer', initials: 'RV', avatarColor: '#7c3aed', task: 'VF — Supermarket', time: 'Feb 19 · 3:00 PM', text: 'Please change apple juice from C to I — audio was unclear.' },
          ]
        },
        {
          id: 4, participantId: 'ACT004', type: 'Baseline',
          date: 'Feb 20, 2025', status: 'pending', statusLabel: 'Pending',
          tasks: [
            { name: 'Clock Drawing', status: 'pending', statusLabel: 'Pending', openRequests: 0 },
            { name: 'CASI', status: 'pending', statusLabel: 'Pending', openRequests: 0 },
            { name: 'Verbal Fluency - Supermarket', status: 'pending', statusLabel: 'Pending', openRequests: 0 },
            { name: 'Verbal Fluency - Letter F', status: 'pending', statusLabel: 'Pending', openRequests: 0 },
            { name: 'Trail Making A', status: 'pending', statusLabel: 'Pending', openRequests: 0 },
            { name: 'Trail Making B', status: 'pending', statusLabel: 'Pending', openRequests: 0 },
          ],
          history: [],
          comments: []
        },
        {
          id: 5, participantId: 'ACT005', type: 'Biennial',
          date: 'Feb 22, 2025', status: 'pending', statusLabel: 'Pending',
          tasks: [
            { name: 'Clock Drawing', status: 'pending', statusLabel: 'Pending', openRequests: 0 },
            { name: 'CASI', status: 'pending', statusLabel: 'Pending', openRequests: 0 },
            { name: 'Verbal Fluency - Supermarket', status: 'pending', statusLabel: 'Pending', openRequests: 0 },
            { name: 'Verbal Fluency - Letter F', status: 'pending', statusLabel: 'Pending', openRequests: 0 },
            { name: 'Trail Making A', status: 'pending', statusLabel: 'Pending', openRequests: 0 },
            { name: 'Trail Making B', status: 'pending', statusLabel: 'Pending', openRequests: 0 },
          ],
          history: [],
          comments: []
        },
        {
          id: 6, participantId: 'ACT006', type: 'Baseline',
          date: 'Mar 4, 2023', status: 'complete', statusLabel: 'Complete',
          tasks: [
            { name: 'Clock Drawing', status: 'scored', statusLabel: 'Scored', openRequests: 0 },
            { name: 'CASI', status: 'scored', statusLabel: 'Scored', openRequests: 0 },
            { name: 'Verbal Fluency - Supermarket', status: 'scored', statusLabel: 'Scored', openRequests: 0 },
            { name: 'Verbal Fluency - Letter F', status: 'scored', statusLabel: 'Scored', openRequests: 0 },
            { name: 'Trail Making A', status: 'scored', statusLabel: 'Scored', openRequests: 0 },
            { name: 'Trail Making B', status: 'scored', statusLabel: 'Scored', openRequests: 0 },
          ],
          history: [
            { id: 'h1', actor: 'Interviewer', initials: 'IN', actorColor: '#317391', type: 'score-entry', time: 'Mar 4 · 10:00 AM', text: 'Scored VF — Supermarket.', from: null, to: null, task: '' },
          ],
          comments: []
        },
      ]
    };
  },
  computed: {
    statusFilterOptions() {
      // QA doesn't see "Pending" in the list at all; still show it in filter for Interviewer
      const base = [
        { value: 'all', label: 'All' },
        { value: 'pending', label: 'Pending' },
        { value: 'submitted', label: 'Ready for QA' },
        { value: 'review-needed', label: 'Review needed' },
        { value: 'complete', label: 'Complete' }
      ];
      if (this.currentRole.id === 'qa') {
        return base.filter(o => o.value !== 'pending');
      }
      return base;
    },
    filteredVisits() {
      let list = this.visits.slice();

      // QA: exclude pending visits entirely
      if (this.currentRole.id === 'qa') {
        list = list.filter(v => v.status !== 'pending');
      }

      if (this.visitTypeFilter !== 'all') {
        list = list.filter(v => v.type === this.visitTypeFilter);
      }
      if (this.statusFilter !== 'all') {
        list = list.filter(v => v.status === this.statusFilter);
      }

      const q = (this.subjectIdSearch || '').trim().toLowerCase();
      if (q) {
        list = list.filter(v => v.participantId.toLowerCase().includes(q));
      }

      return list;
    }
  },
  watch: {
    currentRole(role) {
      if (role.id === 'qa' && this.statusFilter === 'pending') {
        this.statusFilter = 'all';
      }
    }
  },
  methods: {
    selectVisit(visit) {
      this.selectedVisit = visit;
      this.activeTab = 'tasks';
    },
    eventLabel(type) {
      return {
        'score-entry': 'Score Entry',
        'comment': 'Comment',
        'request': 'Change Request',
        'accepted': 'Accepted',
        'rejected': 'Rejected',
        'edit': 'Edit'
      }[type] || type;
    }
  }
};
</script>

<style scoped>
.mockup-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 20px;
  border-bottom: 1px solid #e5e7eb;
  font-size: 13px;
  color: #6b7280;
}
.mockup-badge {
  font-size: 10px;
  font-weight: 700;
  background: #fef3c7;
  color: #92400e;
  padding: 1px 6px;
  border-radius: 3px;
  letter-spacing: 0.05em;
}
.mockup-bar-sep { color: #d1d5db; }
.mockup-bar-label { color: #9ca3af; }
.role-btn {
  padding: 4px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: white;
  font-size: 13px;
  color: #555;
  cursor: pointer;
}
.role-btn:hover { background: #f9fafb; color: #237091; }
.role-btn.active { background: #3986A8; color: white; border-color: #3986A8; }
.mockup-back { margin-left: auto; color: #9ca3af; text-decoration: none; font-size: 12px; }
.mockup-back:hover { color: #64748b; }
.mockup-full {
  margin-left: 12px;
  margin-right: 0;
  color: #9ca3af;
  text-decoration: none;
  font-size: 12px;
  font-weight: 700;
}
.mockup-full:hover { color: #64748b; }

.dashboard-wrap {
  max-width: 1100px;
  margin: 30px auto;
  padding: 0 20px;
}
.dashboard-header { margin-bottom: 20px; }
.dashboard-header .heading {
  font-size: 24px;
  font-weight: 600;
  color: #237091;
  margin: 0;
  text-align: left;
}

.filter-row {
  display: flex;
  flex-wrap: wrap;
  gap: 24px 32px;
  margin-bottom: 20px;
  align-items: flex-start;
}
.filter-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.filter-label {
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.filter-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.filter-chip {
  padding: 6px 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: white;
  font-size: 13px;
  color: #555;
  cursor: pointer;
}
.filter-chip:hover { background: #f9fafb; color: #237091; }
.filter-chip.active { background: #3986A8; color: white; border-color: #3986A8; }

.browse-layout {
  display: grid;
  grid-template-columns: 280px 1fr;
  gap: 16px;
  align-items: start;
}

.visit-list-col {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.search-row {
  flex-shrink: 0;
}
.subject-search {
  width: 100%;
  padding: 8px 12px;
  font-size: 14px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  box-sizing: border-box;
}
.subject-search:focus {
  outline: none;
  border-color: #3986A8;
}
.subject-search::placeholder {
  color: #9ca3af;
}

.visit-list {
  border: 1px solid #a0a0a0;
  border-radius: 6px;
  overflow: hidden;
  background: #F4F4F4;
}
.visit-row {
  padding: 12px 14px;
  border-bottom: 1px solid #d1d5db;
  cursor: pointer;
  transition: background 0.12s;
}
.visit-row:last-child { border-bottom: none; }
.visit-row:hover { background: #e8f4f8; }
.visit-row.active { background: #d0eaf4; border-left: 3px solid #3986A8; }
.visit-row-top { display: flex; justify-content: space-between; align-items: center; margin-bottom: 4px; }
.visit-pid { font-weight: bold; font-size: 14px; color: #237091; }
.visit-type { font-size: 12px; color: #555; }
.visit-row-bottom { display: flex; justify-content: space-between; align-items: center; }
.visit-date { font-size: 12px; color: #777; }

.visit-status {
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
}
.visit-status.complete    { background: #dcfce7; color: #166534; }
.visit-status.pending     { background: #f1f5f9; color: #475569; }
.visit-status.submitted   { background: #dbeafe; color: #1d4ed8; }
.visit-status.review-needed   { background: #fef3c7; color: #92400e; }

.visit-list-empty {
  padding: 24px 14px;
  text-align: center;
  color: #6b7280;
  font-size: 14px;
}

.visit-detail {
  border: 1px solid #a0a0a0;
  border-radius: 6px;
  background: #F4F4F4;
  overflow: hidden;
}
.visit-detail.empty-detail {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  color: #aaa;
  font-size: 14px;
}
.detail-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 16px 18px 12px;
  border-bottom: 1px solid #d1d5db;
  background: white;
}
.detail-title { font-size: 16px; font-weight: bold; color: #237091; margin-bottom: 4px; }
.detail-sub { font-size: 12px; color: #777; }
.go-to-task-link {
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 600;
  color: #3986A8;
  background: transparent;
  border: 1px solid #3986A8;
  border-radius: 6px;
  cursor: pointer;
}
.go-to-task-link:hover { background: #e8f4f8; }

.tabs {
  display: flex;
  border-bottom: 1px solid #d1d5db;
  background: white;
}
.tab-btn {
  padding: 9px 18px;
  border: none;
  border-bottom: 3px solid transparent;
  background: none;
  font-size: 14px;
  color: #555;
  cursor: pointer;
  margin-bottom: -1px;
  transition: color 0.15s, border-color 0.15s;
}
.tab-btn:hover { color: #237091; }
.tab-btn.active { color: #237091; border-bottom-color: #3986A8; font-weight: 600; }

.tab-panel { padding: 16px 18px; }

.task-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}
.task-table th {
  text-align: left;
  padding: 6px 10px;
  border-bottom: 2px solid #d1d5db;
  color: #555;
  font-weight: 600;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.task-table td {
  padding: 8px 10px;
  border-bottom: 1px solid #e5e7eb;
  color: #333;
}
.task-table tr:last-child td { border-bottom: none; }
.task-status {
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
}
.task-status.scored  { background: #dcfce7; color: #166534; }
.task-status.pending { background: #f1f5f9; color: #475569; }
.open-req-chip { font-size: 11px; font-weight: 600; background: #fef3c7; color: #92400e; padding: 2px 8px; border-radius: 10px; }
.none-chip { color: #aaa; font-size: 13px; }

.empty-state { color: #aaa; font-size: 14px; text-align: center; padding: 24px 0; }
.history-event { display: flex; gap: 12px; margin-bottom: 16px; }
.history-event:last-child .he-spine { display: none; }
.he-left { display: flex; flex-direction: column; align-items: center; }
.he-avatar {
  width: 30px; height: 30px; border-radius: 50%; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  font-size: 11px; font-weight: 700; color: white;
}
.he-spine { flex: 1; width: 2px; background: #e5e7eb; margin-top: 4px; min-height: 16px; }
.he-body { flex: 1; padding-bottom: 4px; }
.he-meta { display: flex; align-items: center; gap: 8px; margin-bottom: 3px; flex-wrap: wrap; }
.he-actor { font-weight: 600; font-size: 13px; color: #111; }
.he-tag {
  font-size: 11px; font-weight: 600; padding: 1px 7px; border-radius: 10px;
}
.he-tag.score-entry { background: #dbeafe; color: #1d4ed8; }
.he-tag.comment     { background: #cffafe; color: #0e7490; }
.he-tag.request     { background: #fef3c7; color: #92400e; }
.he-tag.accepted    { background: #dcfce7; color: #166534; }
.he-tag.rejected    { background: #fee2e2; color: #991b1b; }
.he-tag.edit        { background: #ede9fe; color: #5b21b6; }
.he-time { font-size: 11px; color: #9ca3af; margin-left: auto; }
.he-text { font-size: 13px; color: #374151; margin-bottom: 4px; }
.he-change {
  display: inline-flex; align-items: center; gap: 6px;
  background: #f8fafc; border: 1px solid #e2e8f0;
  border-radius: 6px; padding: 3px 10px; font-size: 12px;
}
.he-mark {
  font-weight: 700; padding: 1px 6px; border-radius: 4px;
  background: #f1f5f9; color: #475569;
}
.he-mark.C { background: #dcfce7; color: #166534; }
.he-mark.I { background: #fee2e2; color: #991b1b; }
.he-mark.R { background: #fef3c7; color: #92400e; }
.he-mark.Q { background: #e0f2fe; color: #075985; }
.he-arrow { color: #94a3b8; }
.he-task-ref { font-size: 11px; color: #94a3b8; margin-left: 4px; }

.comment-item {
  display: flex; gap: 10px; margin-bottom: 14px;
  padding: 12px 14px; background: white;
  border: 1px solid #e5e7eb; border-radius: 8px;
}
.comment-avatar {
  width: 32px; height: 32px; border-radius: 50%; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  font-size: 12px; font-weight: 700; color: white;
}
.comment-body { flex: 1; min-width: 0; }
.comment-meta { display: flex; align-items: center; gap: 8px; margin-bottom: 4px; flex-wrap: wrap; }
.comment-author { font-weight: 600; font-size: 13px; color: #111; }
.comment-task { font-size: 11px; background: #f1f5f9; color: #475569; padding: 1px 7px; border-radius: 8px; }
.comment-time { font-size: 11px; color: #9ca3af; margin-left: auto; }
.comment-text { font-size: 13px; color: #374151; }
</style>
